/**
 * Helpers that wrap a `pay_order`-shaped call with the 2FA prompt
 * flow. The server returns 428 + `requires_totp` when the user has
 * 2FA gated on purchases; we open the prompt, retry with the code,
 * and on a wrong-code 428 we re-prompt with the error visible.
 *
 * `payWithTotp(payFn)` is for one-shot purchases (CategoryDetail Buy
 * row, SteamGifts buy, SteamTopup). `payBulkWithTotp` is for the
 * bulk-buy bar — asks once, reuses the same code across N orders.
 */

import { openTotpPrompt } from "@/components/TotpPrompt";

import { isPayRequiresTotp } from "./orders";

type PayFn<T> = (totpCode?: string) => Promise<T>;

/**
 * Run `payFn` once. If the server demands a 2FA code, prompt the
 * user and retry. Wrong code → re-prompt with an inline error.
 * Cancel returns `null`.
 */
export async function payWithTotp<T>(
  payFn: PayFn<T>,
  promptOpts?: { title?: string; description?: string },
): Promise<T | null> {
  let code: string | undefined;
  let retryError: string | undefined;

  for (;;) {
    try {
      return await payFn(code);
    } catch (err) {
      if (!isPayRequiresTotp(err)) throw err;

      // Either no code provided yet, or the code we just sent was
      // wrong. Either way, ask (again).
      const next = await openTotpPrompt({
        title: promptOpts?.title ?? "Confirm purchase",
        description:
          promptOpts?.description ??
          "Enter the 6-digit code from your authenticator to confirm.",
        errorMessage: code ? "Wrong code — try again." : retryError,
      });
      if (next === null) return null;
      code = next;
      retryError = undefined;
    }
  }
}

/**
 * Bulk-buy variant: ask for the code **once**, then run `bodies`
 * sequentially passing the same code into each call. If the first
 * call fails on a wrong code we re-prompt; subsequent items reuse
 * whatever code finally worked. The TOTP step is ±1 (≈90 s window),
 * so even a long bulk usually fits.
 *
 * Returns the array of per-item results (or rejection).
 */
export async function payBulkWithTotp<TItem, TResult>(
  items: TItem[],
  payOne: (item: TItem, totpCode?: string) => Promise<TResult>,
  needTotp: () => boolean,
): Promise<TResult[]> {
  const results: TResult[] = [];
  let code: string | undefined;
  let asked = false;

  for (const item of items) {
    let attemptCode = code;
    for (;;) {
      try {
        const r = await payOne(item, attemptCode);
        results.push(r);
        // Remember the code that just worked for the next items.
        if (attemptCode) code = attemptCode;
        break;
      } catch (err) {
        if (!isPayRequiresTotp(err)) throw err;
        // Server demanded TOTP (or rejected the one we tried).
        const next = await openTotpPrompt({
          title:
            !asked && needTotp()
              ? "Confirm bulk purchase"
              : "Confirm purchase",
          description:
            !asked && needTotp()
              ? "Enter the 6-digit code from your authenticator. We'll use it for every order in this batch."
              : "Enter the 6-digit code from your authenticator to confirm.",
          errorMessage: attemptCode ? "Wrong code — try again." : undefined,
        });
        asked = true;
        if (next === null) return results;
        attemptCode = next;
      }
    }
  }
  return results;
}
